import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

/**
 * Dumb loading spinner. The OAuth `#session_id=…` exchange is now owned by
 * `checkAuth()` inside `AuthProvider` (see lib/auth.jsx) — this component
 * makes ZERO API calls and never touches setUser/setStudio.
 *
 * Its only job: render a "signing you in…" placeholder while
 * `AuthProvider.loading` is true, then redirect once the auth flow has
 * resolved (success → /dashboard, failure → / so the login page shows).
 *
 * No local toast on failure: the global axios 403 interceptor already
 * surfaces "You don't have access to that section." for a no-roster
 * reject, which is the only failure that's actually actionable for the
 * user. A second "Sign in failed. Please try again." here is redundant
 * (and was firing spuriously on any state transition that briefly saw
 * `!loading && !user`).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loading, user } = useAuth();

  useEffect(() => {
    if (loading) return;
    navigate(user ? "/dashboard" : "/", { replace: true });
  }, [loading, user, navigate]);

  return (
    <div
      data-testid="auth-callback-screen"
      className="min-h-screen w-full flex items-center justify-center"
      style={{ backgroundColor: "var(--brand-paper)" }}
    >
      <div className="text-center">
        <div className="kicker mb-3">SIGNING YOU IN</div>
        <div className="font-display text-2xl" style={{ color: "var(--brand-ink)" }}>
          One moment…
        </div>
      </div>
    </div>
  );
}
